/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends HTMLElement {
  /** Footnote-style symbols that should render as superscript. */
  #footnoteSymbols = /[¶†‡§‖※▲]/g;

  connectedCallback() {
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#superscriptFootnoteSymbols(this);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Wraps footnote-style symbols (¶, †, ‡, §, ‖, ※, ▲) in <sup> so they render as superscript.
   * @param {ParentNode} root
   */
  #superscriptFootnoteSymbols(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const value = node.nodeValue;
        if (!value) return NodeFilter.FILTER_REJECT;
        // Leave symbols that are already superscript untouched
        if (node.parentElement?.closest('sup')) return NodeFilter.FILTER_REJECT;
        this.#footnoteSymbols.lastIndex = 0;
        return this.#footnoteSymbols.test(value) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    /** @type {Text[]} */
    const textNodes = [];
    let current;
    while ((current = walker.nextNode())) {
      if (current instanceof Text) textNodes.push(current);
    }

    textNodes.forEach((textNode) => this.#wrapSymbols(textNode));
  }

  /**
   * Replaces a text node's footnote symbols with <sup>-wrapped equivalents.
   * @param {Text} textNode
   */
  #wrapSymbols(textNode) {
    const text = textNode.nodeValue;
    if (!text) return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match;
    this.#footnoteSymbols.lastIndex = 0;

    while ((match = this.#footnoteSymbols.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      const sup = document.createElement('sup');
      sup.textContent = match[0];
      fragment.appendChild(sup);
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
